import React, { useEffect } from 'react';
import { doc, setDoc, deleteDoc, onSnapshot, serverTimestamp } from '../lib/auditedFirestore';
import { db } from '../lib/firebase';
// import Spinner from './Spinner'; // If you have a Spinner component, otherwise use the inline one below

// Simple inline Spinner component if you don't have one
const Spinner = ({ color }) => (
    <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${color || 'border-blue-500'}`}></div>
);

const WaitingRoom = ({ exam, onStart, user, userData }) => {
    
    // This effect registers and unregisters the student from the waiting room
    useEffect(() => {
        if (!user || !userData) {
            console.error("User data not available for waiting room.");
            return;
        }

        const waitingRoomRef = doc(db, 'exams', exam.id, 'waitingRoom', user.uid);
        
        // Register student in waiting room
        setDoc(waitingRoomRef, {
            joinedAt: serverTimestamp(),
            email: user.email,
            name: userData.name || 'Student',
            uid: user.uid // Good to have UID explicitly in the data too
        }).catch(err => console.error("Failed to join waiting room:", err));

        // Unregister on component unmount (e.g., tab close or navigating away)
        return () => {
            // We use a small check to ensure we don't delete if the component unmounted because the exam started
            // However, your logic below handles the specific "onStart" deletion, so this is for
            // premature leaving (closing tab).
            deleteDoc(waitingRoomRef).catch(err => console.error("Failed to leave waiting room on unmount:", err));
        };
    }, [exam.id, user, userData]);

    // This effect listens for the exam to be started by the admin
    useEffect(() => {
        if (!user) return;

        const examRef = doc(db, 'exams', exam.id);
        const unsubscribe = onSnapshot(examRef, (docSnap) => {
            if (docSnap.exists()) {
                const examData = docSnap.data();
                if (examData.invigilationStatus === 'active') {
                    // Exam started! Clean up the waiting room doc and then start the exam.
                    const waitingRoomRef = doc(db, 'exams', exam.id, 'waitingRoom', user.uid);
                    deleteDoc(waitingRoomRef)
                        .catch(err => console.error("Failed to leave waiting room on start:", err))
                        .finally(() => {
                            onStart(); // Trigger the dashboard to switch to ExamTaker
                        });
                }
            }
        });
        
        // Clean up the listener
        return () => unsubscribe();
    }, [exam.id, onStart, user.uid]);

    return (
        <div className="flex flex-col items-center justify-center min-h-[50vh] bg-white p-8 rounded-lg shadow-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">{exam.title}</h2>
            <p className="text-xl text-gray-600 mb-6">You are in the waiting room.</p>
            <Spinner color="border-purple-500" />
            <p className="mt-6 text-lg text-gray-500">Please wait for the invigilator to start the exam.</p>
            <p className="text-gray-500">Your exam will begin automatically.</p>
        </div>
    );
};

export default WaitingRoom;